import type { ReactNode } from "react"
import { useEffect } from "react"
import { createPortal } from "react-dom"
import { create } from "zustand"

// A settings page renders its own full-height panel (e.g. the widget preview)
// into the shell, beside the scrolling content, via `SettingsRightRail`.
interface SettingsRightRailState {
	node: HTMLDivElement | null
	mountCount: number
	setNode: (node: HTMLDivElement | null) => void
	mount: () => void
	unmount: () => void
}

const useSettingsRightRailState = create<SettingsRightRailState>((set) => ({
	node: null,
	mountCount: 0,
	setNode: (node) => set({ node }),
	mount: () => set((s) => ({ mountCount: s.mountCount + 1 })),
	unmount: () => set((s) => ({ mountCount: Math.max(0, s.mountCount - 1) })),
}))

export function SettingsRightRailSlot() {
	const setNode = useSettingsRightRailState((s) => s.setNode)
	const isMounted = useSettingsRightRailState((s) => s.mountCount > 0)

	if (!isMounted) return null

	return (
		<div
			ref={setNode}
			className="hidden w-[33.5rem] shrink-0 flex-col overflow-hidden border-l border-gray-200 bg-gray-150 lg:flex dark:border-gray-700/80 dark:bg-gray-850"
		/>
	)
}

export function SettingsRightRail({ children }: { children: ReactNode }) {
	const { node, mount, unmount } = useSettingsRightRailState()

	useEffect(() => {
		mount()
		return unmount
	}, [mount, unmount])

	if (!node) return null

	return createPortal(children, node)
}

export function useHasSettingsRightRail() {
	return useSettingsRightRailState((s) => s.mountCount > 0)
}
